import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { rulesetRegistry, getRulesetConfig, type RulesetConfig } from '@character-creator/data';
import { useCharacterCreationStore } from '../stores/characterCreationStore';

const DEFAULT_RULESET = 'srd-5.1';

export interface UseUrlRulesetResult {
  /** Current ruleset ID from URL (validated) */
  rulesetId: string;
  /** Whether the URL ruleset is valid */
  isValid: boolean;
  /** Current ruleset config */
  config: RulesetConfig;
}

/**
 * Hook to read and validate ruleset from URL parameters.
 *
 * - Reads `:ruleset` param from URL
 * - Validates it exists in rulesetRegistry
 * - Syncs to Zustand store for components that need it
 * - Redirects to picker page if invalid
 *
 * @example
 * ```tsx
 * // In a component under /:ruleset route
 * const { rulesetId, config } = useUrlRuleset();
 * console.log(config.name); // "SRD 5.1" or "Ignus"
 * ```
 */
export function useUrlRuleset(): UseUrlRulesetResult {
  const { ruleset } = useParams<{ ruleset: string }>();
  const navigate = useNavigate();
  const setRulesetId = useCharacterCreationStore((s) => s.setRulesetId);

  // Validate ruleset exists in registry
  const isValid = Boolean(ruleset && ruleset in rulesetRegistry);
  const rulesetId = isValid && ruleset ? ruleset : DEFAULT_RULESET;

  const config = useMemo(() => {
    return getRulesetConfig(rulesetId) ?? getRulesetConfig(DEFAULT_RULESET)!;
  }, [rulesetId]);

  // Sync to store on mount/change (for components that still read from store)
  useEffect(() => {
    if (isValid) {
      setRulesetId(rulesetId);
    }
  }, [rulesetId, isValid, setRulesetId]);

  // Redirect invalid rulesets to picker
  useEffect(() => {
    if (ruleset && !isValid) {
      navigate('/', { replace: true });
    }
  }, [ruleset, isValid, navigate]);

  return { rulesetId, isValid, config };
}

/**
 * Hook to get the ruleset ID from URL without side effects.
 * Use this in components that just need to read the value.
 */
export function useRulesetIdFromUrl(): string {
  const { ruleset } = useParams<{ ruleset: string }>();
  const isValid = Boolean(ruleset && ruleset in rulesetRegistry);
  return isValid && ruleset ? ruleset : DEFAULT_RULESET;
}
